import { FC } from 'react';
import { twJoin } from 'tailwind-merge';

import Button from 'molecules/Button';
import MarketoForm from 'molecules/Form';
import Image from 'molecules/Image';
import RichText from 'molecules/RichText';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';
import { ContainedConversionPanelConfigProps } from 'components/ContainedConversionPanel/config';
import { bodyStyles, cardStyles, headingStyles } from 'components/ContainedConversionPanel/styles';

import { useBuilderReference } from 'utils/hooks/useBuilderReference';
import HeadingHighlights from 'utils/strings/HeadingHighlights';

const ContainedConversionPanel: FC<ContainedConversionPanelConfigProps> = props => {
  const { sectionProps, rest } = splitSectionProps(props);
  const {
    heading,
    body,
    image,
    button,
    useForm,
    form,
    onFormSuccess,
    formLayout = 'stacked',
    lockedContentPageId,
    theme = 'default',
  } = rest;

  const hasImage = !!image?.file?.url;
  // The image variant is the dark "guide" CTA from the design, so it always uses the
  // canonical dark card recipe regardless of the (non-editable) theme prop.
  const cardTheme = hasImage ? 'dark' : theme;

  const hasPreloadedForm = !!form?.value?.data;
  const formRefData = useBuilderReference(
    !hasPreloadedForm ? form?.model : undefined,
    !hasPreloadedForm ? form?.id : undefined,
  );
  const formData = hasPreloadedForm ? form.value.data : formRefData;

  const isTeal = theme === 'teal';

  const mergedSectionProps = {
    ...sectionProps,
    ...(isTeal && { tealBackground: true }),
  };

  const textBlock = (
    <>
      {heading && (
        <h2 className={headingStyles({ theme: cardTheme })}>
          <HeadingHighlights heading={heading} />
        </h2>
      )}
      {body && (
        <RichText text={body} className={twJoin(bodyStyles({ theme: cardTheme }), hasImage && 'hidden lg:block')} />
      )}
    </>
  );

  return (
    <Section {...mergedSectionProps} id={sectionProps.id || 'newsletterHidden'}>
      <div className="relative">
        <ScrollFadeSection delay={0.2}>
          <div className={cardStyles({ theme: cardTheme })}>
            {hasImage ? (
              <div className="flex flex-row items-center gap-5 lg:max-w-[700px] lg:gap-8">
                <Image
                  url={image?.file?.url}
                  alt={image?.alt || heading}
                  className="h-[120px] w-[92px] shrink-0 overflow-hidden rounded-md lg:h-[150px] lg:w-[116px] [&_img]:object-contain"
                  sizes="116px"
                  unsetRatio
                  unsetMaxW
                />
                <div className="flex flex-col gap-4">{textBlock}</div>
              </div>
            ) : (
              <div className="flex flex-col gap-4 lg:max-w-[700px]">{textBlock}</div>
            )}

            {(formData as FormReference)
              ? useForm && (
                  <div className={twJoin('flex w-full shrink-0 flex-col sm:w-[350px]', hasImage && 'lg:self-center')}>
                    <MarketoForm
                      formData={formData as FormReference}
                      callback={onFormSuccess}
                      layout={formLayout}
                      lockedContentPageId={lockedContentPageId}
                    />
                  </div>
                )
              : button && (
                  <div className={twJoin('flex w-full shrink-0 flex-col sm:w-[286px]', hasImage && 'lg:self-center')}>
                    <Button
                      {...button}
                      className="!w-full !bg-buttonLightGradient !text-gray-900 hover:!bg-primary-button-hover"
                    />
                  </div>
                )}
          </div>
        </ScrollFadeSection>
      </div>
    </Section>
  );
};

export default ContainedConversionPanel;
